import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Pencil } from 'lucide-react';
import { FieldSectionLabel } from '@bliks-io/ui';
import type { CapacitySources, ProcessDraftStep } from '@bliks-io/contracts';
import { CapacitySourceRow } from '@/components/capacity-cell';
import { useProcessDraft, type ProcessDraftQueryStatus } from '@/api/queries/useProcessDraft';
import { useProcess } from '@/api/queries/useProcesses';
import { DraftStepManualCapacityDialog } from './DraftStepManualCapacityDialog';
import { manualCapacityLabel } from './manualCapacityLabel';

interface Props {
  step: ProcessDraftStep;
  processId: string;
}

/**
 * The capacity block of a manual draft step (PRD 8.2, Variant C): the erhoben
 * and the manuell value side by side, "wirksam" on the one the tenant's
 * Kapazitätsquelle lets win. Sensitive data — renders nothing unless this
 * editor may read it, which is also the audience allowed to write it.
 */
export function DraftStepCapacitySection({ step, processId }: Props) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const canViewSensitiveFields = useProcess(processId).data?.capabilities.canViewSensitiveFields;
  const draftData: ProcessDraftQueryStatus | undefined = useProcessDraft(processId).data;
  const draft = draftData !== 'not-found' ? draftData : undefined;
  const stepId = String(step.stableStepId);
  const effective = draft?.stepCapacities?.[stepId];
  const sources: CapacitySources | undefined = draft?.stepCapacitySources?.[stepId];

  const manualValue = step.manualCapacity ?? undefined;
  // A step with no live recording has no team-extrapolated figure yet, so
  // there is nothing for either hint to be about.
  const manualHasFigure = !!manualValue && !!sources?.manual;
  const shadowed = manualHasFigure && effective?.capacitySource === 'survey';
  // No winner at all: "Nur erhoben" on a step the survey never answered, where
  // the setting — not a competing figure — is what keeps the manual value out.
  const excludedBySetting = manualHasFigure && !effective;

  if (!canViewSensitiveFields) return null;

  return (
    <div className="mt-4">
      <FieldSectionLabel icon={Clock}>{t('processDraft.capacity.title')}</FieldSectionLabel>
      <div className="mt-2 space-y-1.5">
        <CapacitySourceRow
          label={t('processes.capacitySourceSurvey')}
          binding={sources?.survey ?? null}
          effective={effective?.capacitySource === 'survey'}
        />
        <CapacitySourceRow
          label={t('processes.capacitySourceManual')}
          binding={sources?.manual ?? null}
          effective={effective?.capacitySource === 'manual'}
          // A step created in this draft has no live recording to extrapolate
          // over, so the entered primitives are all there is to show.
          fallbackText={
            manualValue && !sources?.manual ? manualCapacityLabel(manualValue, t) : undefined
          }
          action={
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              aria-label={manualValue ? t('common.edit') : t('processDraft.capacity.enter')}
              className="shrink-0 rounded-md p-1 text-fg-subtle transition-colors hover:bg-muted hover:text-fg-secondary"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          }
        />
      </div>
      {shadowed && (
        <p className="mt-2 text-xs text-warning">{t('processDraft.capacity.shadowHint')}</p>
      )}
      {excludedBySetting && (
        <p className="mt-2 text-xs text-warning">
          {t('processDraft.capacity.excludedBySettingHint')}
        </p>
      )}
      {dialogOpen && (
        <DraftStepManualCapacityDialog
          open
          onOpenChange={setDialogOpen}
          step={step}
          processId={processId}
        />
      )}
    </div>
  );
}
